/**
 * AdminHomeLab — Home Simulation Lab + Debug Panel
 * 
 * Admin-only tool to:
 * - Simulate different user states (J1/J2/J3/J5)
 * - Override time of day, session count, theme, personalization
 * - View live decision output from the suggestion engine
 * - View analytics event log
 * - All isolated from real user behavior
 */
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, RotateCcw, Eye, EyeOff, Activity, Zap, Clock, Palette, User, CheckCircle, AlertTriangle, TrendingUp } from 'lucide-react';
import { getHomeSuggestionState } from '@/services/homeSuggestionEngine';
import { getAnalyticsLog, clearAnalyticsLog } from '@/services/homeAnalytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SIM_ENABLED_KEY = 'hearen_sim_enabled';
const SIM_CONTEXT_KEY = 'hearen_sim_context';

// ─── Preset configurations ───
const PRESETS = {
  J1: { daysSinceInstall: 0, sessionCount: 0, lastThemeUsed: null, hasCompletedPersonalization: false, timeOfDay: 'journee' },
  J2: { daysSinceInstall: 2, sessionCount: 1, lastThemeUsed: null, hasCompletedPersonalization: false, timeOfDay: 'journee' },
  J3: { daysSinceInstall: 4, sessionCount: 3, lastThemeUsed: 'nature', hasCompletedPersonalization: false, timeOfDay: 'soir' },
  J5: { daysSinceInstall: 7, sessionCount: 6, lastThemeUsed: 'sleep', hasCompletedPersonalization: false, timeOfDay: 'nuit' },
  COMPLETED: { daysSinceInstall: 14, sessionCount: 12, lastThemeUsed: 'meditation', hasCompletedPersonalization: true, timeOfDay: 'matin' },
};

const TIME_OPTIONS = ['matin', 'journee', 'soir', 'nuit'];
const THEME_OPTIONS = [null, 'nature', 'relaxation', 'sleep', 'meditation', 'pulse_flow', 'instrumental', 'lofi'];

const AdminHomeLab = () => {
  const navigate = useNavigate();
  const [simEnabled, setSimEnabled] = useState(() => localStorage.getItem(SIM_ENABLED_KEY) === 'true');
  const [context, setContext] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(SIM_CONTEXT_KEY) || 'null') || PRESETS.J2;
    } catch { return PRESETS.J2; }
  });
  const [analyticsLog, setAnalyticsLog] = useState([]);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [perf, setPerf] = useState(null);

  // Fetch performance metrics
  const fetchPerf = useCallback(async () => {
    try {
      const token = localStorage.getItem('hearen_token');
      const res = await fetch(`${API_URL}/api/analytics/home-performance?hours=24`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) setPerf(await res.json());
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchPerf(); }, [fetchPerf]);

  // Compute suggestion state live
  const result = useMemo(() => {
    return getHomeSuggestionState(context, context);
  }, [context]);

  // Persist simulation state
  useEffect(() => {
    localStorage.setItem(SIM_ENABLED_KEY, simEnabled ? 'true' : 'false');
    if (simEnabled) {
      localStorage.setItem(SIM_CONTEXT_KEY, JSON.stringify(context));
    }
  }, [simEnabled, context]);

  // Load analytics log
  const refreshAnalytics = useCallback(() => {
    setAnalyticsLog(getAnalyticsLog());
  }, []);

  useEffect(() => { refreshAnalytics(); }, [refreshAnalytics]);

  const updateField = (field, value) => {
    setContext(prev => ({ ...prev, [field]: value }));
  };

  const applyPreset = (key) => {
    setContext({ ...PRESETS[key], isFr: context.isFr ?? true, variantIndex: context.variantIndex ?? 0 });
  };

  const resetSim = () => {
    setSimEnabled(false);
    localStorage.removeItem(SIM_ENABLED_KEY);
    localStorage.removeItem(SIM_CONTEXT_KEY);
    setContext(PRESETS.J2);
  };

  const stageColor = {
    J1: '#6b7280', J2: '#6366f1', J3: '#a855f7', J5: '#ec4899', COMPLETED: '#10b981',
  };

  return (
    <div className="min-h-screen" style={{ background: '#0a0a1a' }}>
      {/* Header */}
      <div className="sticky top-0 z-30 px-4 py-3 flex items-center gap-3" style={{ background: 'rgba(10,10,26,0.95)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <button onClick={() => navigate('/admin')} className="p-2 rounded-lg hover:bg-white/5" data-testid="lab-back-btn">
          <ArrowLeft size={18} className="text-white/60" />
        </button>
        <div className="flex-1">
          <h1 className="text-white font-semibold text-base">Home Simulation Lab</h1>
          <p className="text-white/40 text-xs">Test J1/J2/J3/J5 en temps réel</p>
        </div>
        <div className="flex items-center gap-2">
          {simEnabled && (
            <span className="px-2 py-0.5 rounded text-[10px] font-mono" style={{ background: 'rgba(251,191,36,0.15)', color: '#fbbf24' }}>
              SIM ON
            </span>
          )}
          <button
            onClick={() => navigate('/')}
            className="px-3 py-1.5 rounded-lg text-xs font-medium text-white/70 hover:text-white"
            style={{ background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.25)' }}
            data-testid="lab-preview-btn"
          >
            <Eye size={12} className="inline mr-1" /> Preview Home
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 pb-60 space-y-6">

        {/* ─── Performance (24h) ─── */}
        <div className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }} data-testid="performance-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <TrendingUp size={14} className="text-indigo-400" />
              <span className="text-white/70 text-xs font-medium uppercase tracking-wider">Performance (24h)</span>
            </div>
            <button onClick={fetchPerf} className="text-white/30 hover:text-white/50 text-[10px]">Refresh</button>
          </div>

          {!perf ? (
            <p className="text-white/20 text-xs text-center py-2">Chargement...</p>
          ) : perf.total_impressions === 0 ? (
            <p className="text-white/20 text-xs text-center py-2">Aucune donnée sur les dernières 24h</p>
          ) : (
            <div className="space-y-2.5">
              {/* Global CTR */}
              <div className="flex items-center justify-between">
                <span className="text-white/40 text-xs">CTR global</span>
                <span className="text-white/80 text-sm font-semibold font-mono">{perf.global_ctr}%</span>
              </div>

              {/* Per-stage CTR */}
              <div className="flex gap-2">
                {['J2', 'J3', 'J5'].map(stage => {
                  const s = perf.stages?.[stage];
                  if (!s || s.impressions === 0) return (
                    <div key={stage} className="flex-1 rounded-lg py-2 px-2.5 text-center" style={{ background: 'rgba(255,255,255,0.02)' }}>
                      <p className="text-white/25 text-[10px] font-medium">{stage}</p>
                      <p className="text-white/20 text-xs mt-0.5">—</p>
                    </div>
                  );
                  const isHot = s.ctr >= 30;
                  return (
                    <div
                      key={stage}
                      className="flex-1 rounded-lg py-2 px-2.5 text-center"
                      style={{
                        background: isHot ? 'rgba(251,146,60,0.08)' : 'rgba(255,255,255,0.03)',
                        border: isHot ? '1px solid rgba(251,146,60,0.2)' : '1px solid transparent',
                      }}
                    >
                      <p className="text-white/40 text-[10px] font-medium">{stage}</p>
                      <p className={`text-sm font-semibold font-mono mt-0.5 ${isHot ? 'text-orange-400' : 'text-white/70'}`}>
                        {s.ctr}%{isHot ? ' 🔥' : ''}
                      </p>
                      <p className="text-white/20 text-[9px] mt-0.5">{s.clicks}/{s.impressions}</p>
                    </div>
                  );
                })}
              </div>

              {/* Completion */}
              <div className="flex items-center justify-between pt-1" style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                <span className="text-white/40 text-xs">Completion</span>
                <span className="text-emerald-400/80 text-sm font-semibold font-mono">{perf.completion_rate}%</span>
              </div>

              {/* Best variant */}
              {perf.best_variant && (
                <div className="flex items-center justify-between pt-1" style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                  <span className="text-white/40 text-xs">Best variant</span>
                  <span className="text-xs">
                    <span className="text-white/50">"{perf.best_variant.title}"</span>
                    <span className="text-white/70 font-semibold font-mono ml-1.5">→ {perf.best_variant.ctr}%</span>
                    {perf.best_variant.ctr >= 30 && <span className="ml-1">🔥</span>}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ─── Simulation Toggle ─── */}
        <div className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }} data-testid="sim-toggle-section">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap size={16} className="text-amber-400" />
              <span className="text-white/80 text-sm font-medium">Activer Simulation</span>
            </div>
            <button
              onClick={() => setSimEnabled(!simEnabled)}
              className={`relative w-11 h-6 rounded-full transition-colors ${simEnabled ? 'bg-amber-500' : 'bg-white/10'}`}
              data-testid="sim-toggle"
            >
              <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${simEnabled ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
            </button>
          </div>
          {simEnabled && (
            <p className="text-amber-400/60 text-xs mt-2 flex items-center gap-1">
              <AlertTriangle size={10} /> La Home utilise les paramètres simulés ci-dessous
            </p>
          )}
        </div>

        {/* ─── Quick Presets ─── */}
        <div className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }} data-testid="presets-section">
          <p className="text-white/60 text-xs font-medium mb-3 uppercase tracking-wider">Presets rapides</p>
          <div className="flex flex-wrap gap-2">
            {Object.keys(PRESETS).map(key => (
              <button
                key={key}
                onClick={() => applyPreset(key)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:scale-105"
                style={{
                  background: `${stageColor[key]}15`,
                  color: stageColor[key],
                  border: `1px solid ${stageColor[key]}30`,
                }}
                data-testid={`preset-${key.toLowerCase()}`}
              >
                {key}
              </button>
            ))}
          </div>
        </div>

        {/* ─── Simulation Controls ─── */}
        <div className="rounded-xl p-4 space-y-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }} data-testid="controls-section">
          <p className="text-white/60 text-xs font-medium uppercase tracking-wider">Paramètres simulés</p>

          {/* Time of day */}
          <div className="flex items-center justify-between">
            <label className="text-white/50 text-xs flex items-center gap-1.5"><Clock size={12} /> Moment</label>
            <div className="flex gap-1">
              {TIME_OPTIONS.map(t => (
                <button
                  key={t}
                  onClick={() => updateField('timeOfDay', t)}
                  className={`px-2.5 py-1 rounded text-xs transition-all ${context.timeOfDay === t ? 'bg-indigo-500/30 text-indigo-300 border border-indigo-500/40' : 'bg-white/5 text-white/40 border border-transparent'}`}
                  data-testid={`time-${t}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Session count */}
          <div className="flex items-center justify-between">
            <label className="text-white/50 text-xs flex items-center gap-1.5"><Activity size={12} /> Sessions</label>
            <div className="flex items-center gap-2">
              <button onClick={() => updateField('sessionCount', Math.max(0, context.sessionCount - 1))} className="w-7 h-7 rounded bg-white/5 text-white/50 text-sm hover:bg-white/10">-</button>
              <span className="text-white/80 text-sm font-mono w-6 text-center" data-testid="session-count-display">{context.sessionCount}</span>
              <button onClick={() => updateField('sessionCount', context.sessionCount + 1)} className="w-7 h-7 rounded bg-white/5 text-white/50 text-sm hover:bg-white/10">+</button>
            </div>
          </div>

          {/* Days since install */}
          <div className="flex items-center justify-between">
            <label className="text-white/50 text-xs flex items-center gap-1.5"><User size={12} /> Jours</label>
            <div className="flex items-center gap-2">
              <button onClick={() => updateField('daysSinceInstall', Math.max(0, context.daysSinceInstall - 1))} className="w-7 h-7 rounded bg-white/5 text-white/50 text-sm hover:bg-white/10">-</button>
              <span className="text-white/80 text-sm font-mono w-6 text-center" data-testid="days-display">{context.daysSinceInstall}</span>
              <button onClick={() => updateField('daysSinceInstall', context.daysSinceInstall + 1)} className="w-7 h-7 rounded bg-white/5 text-white/50 text-sm hover:bg-white/10">+</button>
            </div>
          </div>

          {/* Last theme */}
          <div className="flex items-center justify-between">
            <label className="text-white/50 text-xs flex items-center gap-1.5"><Palette size={12} /> Thème</label>
            <select
              value={context.lastThemeUsed || ''}
              onChange={(e) => updateField('lastThemeUsed', e.target.value || null)}
              className="bg-white/5 text-white/70 text-xs rounded px-2 py-1.5 border border-white/10 outline-none"
              data-testid="theme-select"
            >
              <option value="">Aucun</option>
              {THEME_OPTIONS.filter(Boolean).map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          {/* Personalization completed */}
          <div className="flex items-center justify-between">
            <label className="text-white/50 text-xs flex items-center gap-1.5"><CheckCircle size={12} /> Personnalisation</label>
            <button
              onClick={() => updateField('hasCompletedPersonalization', !context.hasCompletedPersonalization)}
              className={`relative w-11 h-6 rounded-full transition-colors ${context.hasCompletedPersonalization ? 'bg-emerald-500' : 'bg-white/10'}`}
              data-testid="perso-toggle"
            >
              <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${context.hasCompletedPersonalization ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
            </button>
          </div>

          {/* Language */}
          <div className="flex items-center justify-between">
            <label className="text-white/50 text-xs">Langue</label>
            <div className="flex gap-1">
              {[true, false].map(v => (
                <button
                  key={v.toString()}
                  onClick={() => updateField('isFr', v)}
                  className={`px-3 py-1 rounded text-xs ${context.isFr === v ? 'bg-indigo-500/30 text-indigo-300' : 'bg-white/5 text-white/40'}`}
                >
                  {v ? 'FR' : 'EN'}
                </button>
              ))}
            </div>
          </div>

          {/* Reset */}
          <button
            onClick={resetSim}
            className="w-full mt-2 py-2 rounded-lg text-xs font-medium text-white/40 hover:text-white/60 flex items-center justify-center gap-1.5"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
            data-testid="reset-sim-btn"
          >
            <RotateCcw size={12} /> Reset Simulation
          </button>
        </div>

        {/* ─── Live Decision Output (Debug Panel §5) ─── */}
        <div className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }} data-testid="debug-panel">
          <p className="text-white/60 text-xs font-medium uppercase tracking-wider mb-3">Résultat moteur</p>

          <div className="space-y-2">
            <Row label="Visible" value={result.visible ? 'OUI' : 'NON'} color={result.visible ? '#10b981' : '#ef4444'} />
            <Row label="Stage" value={result.stage || '-'} color={stageColor[result.stage] || '#6b7280'} />
            <Row label="Variant ID" value={result.variantId || '-'} />
            <Row label="Reason" value={result.reason} />
            <Row label="Titre" value={result.title || '(aucun)'} highlight />
            <Row label="Sous-titre" value={result.subtitle || '(aucun)'} />
            <Row label="CTA" value={result.cta || '(aucun)'} />
            <Row label="Home subtitle" value={result.homeSubtitle} />
            <Row label="Glow tint" value={result.glow?.tint || '-'} />
            <Row label="Pulse speed" value={result.animation?.pulse || '-'} />
          </div>
        </div>

        {/* ─── Analytics Event Log (§4/§5) ─── */}
        <div className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }} data-testid="analytics-section">
          <div className="flex items-center justify-between mb-3">
            <p className="text-white/60 text-xs font-medium uppercase tracking-wider">Analytics Events</p>
            <div className="flex gap-2">
              <button onClick={refreshAnalytics} className="text-white/30 hover:text-white/60 text-xs">Refresh</button>
              <button onClick={() => { clearAnalyticsLog(); refreshAnalytics(); }} className="text-red-400/50 hover:text-red-400 text-xs">Clear</button>
              <button onClick={() => setShowAnalytics(!showAnalytics)} className="text-white/30 hover:text-white/60">
                {showAnalytics ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>

          {showAnalytics && (
            <div className="space-y-1.5 max-h-60 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
              {analyticsLog.length === 0 ? (
                <p className="text-white/20 text-xs text-center py-3">Aucun événement</p>
              ) : (
                analyticsLog.map((evt, i) => (
                  <div key={i} className="flex items-start gap-2 py-1.5 border-b border-white/5">
                    <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[9px] font-mono ${evt.simulation_mode ? 'bg-amber-500/15 text-amber-400' : 'bg-indigo-500/15 text-indigo-300'}`}>
                      {evt.simulation_mode ? 'SIM' : 'REAL'}
                    </span>
                    <span className="text-white/60 text-[10px] font-mono flex-shrink-0 w-28 truncate">{evt.event}</span>
                    <span className="text-white/30 text-[10px]">{evt.stage || '-'} / {evt.variant_id || '-'}</span>
                    <span className="text-white/20 text-[10px] ml-auto">{evt.timestamp ? new Date(evt.timestamp).toLocaleTimeString() : ''}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Helper: Row in debug panel ───
const Row = ({ label, value, color, highlight }) => (
  <div className="flex items-start justify-between gap-3">
    <span className="text-white/30 text-xs flex-shrink-0">{label}</span>
    <span
      className={`text-xs text-right break-all ${highlight ? 'font-medium' : ''}`}
      style={{ color: color || (highlight ? '#e2e8f0' : 'rgba(255,255,255,0.5)') }}
    >
      {value}
    </span>
  </div>
);

export default AdminHomeLab;
